import { FC } from 'react';
import cn from 'classnames';
import Link from 'next/link';
import { AnimatedCard } from '@/components/animation/AnimatedCard';

import { BuyButton } from '@/components/buttons/BuyButton';
import { CardImage } from '@/components/common/CardImage';
import { CategoryLabel } from '@/components/common/CategoryLabel';
import { DrugCardProps } from './DrugCard.props';
import { ButtonLink } from '@/components/buttons/ButtonLink';
import { useLocaleHook } from '@/hooks';
import { AnimatedComponent } from '@/components/common/AnimatedComponent';

export const DrugCard: FC<DrugCardProps> = ({
  title,
  buttonLabel,
  category,
  drugImage,
  className,
  linkUrl = '/',
  isInnerLink = false,
  buyAggregator,
  titleClassName,
}) => {
  const locale = useLocaleHook();
  const isUk = locale === 'uk';

  return (
    <AnimatedCard
      className={cn(
        'p-4 shadow-base dark:rounded dark:border dark:border-darkFullLight md:flex md:h-full md:flex-col',
        className,
      )}
    >
      <Link
        href={linkUrl}
        prefetch={false}
        aria-label={
          isUk ? 'Перейти на сторінку лікарського засобу' : 'Go to drug page'
        }
      >
        <div className="relative mb-5 aspect-square overflow-hidden bg-gradient-to-b from-grStart to-grEnd p-[38px] pt-[54px] md:p-[46px] lg:aspect-[4/3] ">
          {category && (
            <CategoryLabel
              categoryName={category.name}
              color={category.category.ategoryColor}
              className="absolute left-2.5 top-2.5"
            />
          )}

          <CardImage
            src={drugImage?.mediaItemUrl ?? '/images/image-bg.jpg'}
            alt={drugImage?.altText ?? 'image not found'}
            className="h-full w-full object-contain object-center"
          />
        </div>
      </Link>
      <div className="flex flex-1 flex-col justify-between">
        <p className={cn('mb-4 text-xl md:text-xxl', titleClassName)}>
          {title}
        </p>

        <div className="relative ml-auto flex w-fit gap-4">
          <ButtonLink
            withIcon
            href={linkUrl}
            isInnerLink={isInnerLink}
            aria-label={
              isUk
                ? 'Перейти на сторінку лікарського засобу'
                : 'Go to drug page'
            }
          >
            {buttonLabel}
          </ButtonLink>
          {buyAggregator && buyAggregator?.length > 0 && (
            <div className="group">
              <BuyButton />
              <AnimatedComponent>
                <ul className="invisible absolute right-0 top-full z-20 w-full origin-top-left scale-0 divide-y rounded-base bg-white opacity-0 shadow-accessibility transition-all group-focus-within:visible group-focus-within:scale-100 group-focus-within:opacity-100 group-hover:visible group-hover:scale-100 group-hover:opacity-100 dark:bg-darkFullDark">
                  {buyAggregator?.map(item => {
                    if (item.link && item.pharmacy) {
                      return (
                        <li key={item?.link}>
                          <a
                            href={item?.link}
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                            aria-label={
                              isUk
                                ? 'Перейти на сторінку лікарського засобу'
                                : 'Go to drug page'
                            }
                            className="block border border-white p-4 text-base transition-colors duration-300 hover:border-accentMain focus:border-accentMain dark:border-darkFullDark dark:hover:border-accentMain dark:focus:border-accentMain"
                          >
                            {item?.pharmacy}
                          </a>
                        </li>
                      );
                    }
                  })}
                </ul>
              </AnimatedComponent>
            </div>
          )}
        </div>
      </div>
    </AnimatedCard>
  );
};
